import { DeliveryMethodNames, Firm } from '@eo-storefronts/eo-core'
import { useEffect, useState } from 'react'
import { useEoValue } from '~/src/hooks/useEoState'
import { useTranslations } from '~/src/hooks/useTranslations'
import {
  CHECKOUT_FORM_STATE,
  CheckoutFormState,
  GET_TOTAL_AMOUNT_OF_CHECKOUT_OPTIONS_SELECTOR
} from '~/src/stores/checkout'
import { FIRM_SELECTOR, IS_DELIVERY_METHOD_ENABLED_FOR_FIRM } from '~/src/stores/firm'
import { useFirmAcceptsOrder } from '~/src/hooks/orders/useFirmAcceptsOrder'
import {
  CART_OF_FIRM_SELECTOR,
  CART_SUBTOTAL_WITH_DISCOUNTS_SELECTOR,
  CART_TOTAL_ITEMS_SELECTOR
} from '~/src/stores/cart'
import { AlertColor } from '@mui/material'
import { CATEGORIES_STATE } from '~/src/stores/categories'
import { PRODUCTS_LOYALTY_REWARDS_SELECTOR, PRODUCTS_STATE } from '~/src/stores/product'
import { LOYALTY_DISCOUNT_CAN_BE_APPLIED_SELECTOR, LOYALTY_TYPE_IS_FREE_PRODUCT_SELECTOR } from '~/src/stores/loyalty'
import { LANGUAGE_STATE } from '~/src/stores/lang'
import { checkCategoryAvailability } from '~/src/services/CategoryService'
import { checkProductAvailability } from '~/src/services/ProductService'
import { CartProduct } from '~/src/types/CartProduct'

export interface OrderErrorType {
  variant: AlertColor,
  text: string,
  type?: string,
  closable: boolean,
}

interface ReturnsType {
  errors: OrderErrorType[],
  getErrors(): OrderErrorType[],
}

const useValidateOrder = (): ReturnsType => {
  const categories = useEoValue(CATEGORIES_STATE)
  const products = useEoValue(PRODUCTS_STATE)
  const cart = useEoValue(CART_OF_FIRM_SELECTOR)
  const loyaltyDiscountCanBeApplied = useEoValue(LOYALTY_DISCOUNT_CAN_BE_APPLIED_SELECTOR)
  const loyaltyTypeIsFreeProduct = useEoValue(LOYALTY_TYPE_IS_FREE_PRODUCT_SELECTOR)
  const productsLoyaltyRewards = useEoValue(PRODUCTS_LOYALTY_REWARDS_SELECTOR)
  const lang = useEoValue(LANGUAGE_STATE)
  const firm = useEoValue<Firm | null>(FIRM_SELECTOR)
  const cartTotalItems = useEoValue(CART_TOTAL_ITEMS_SELECTOR)
  const totalAmountOfCart = useEoValue(CART_SUBTOTAL_WITH_DISCOUNTS_SELECTOR)
  const totalAmountOfCheckoutOptions = useEoValue(GET_TOTAL_AMOUNT_OF_CHECKOUT_OPTIONS_SELECTOR)
  const checkoutForm = useEoValue<CheckoutFormState>(CHECKOUT_FORM_STATE)
  const deliverMethodEnabledForFirm = useEoValue(IS_DELIVERY_METHOD_ENABLED_FOR_FIRM)
  const [ errors, setErrors ] = useState<OrderErrorType[]>([])
  const { checkAll } = useFirmAcceptsOrder()
  const { t } = useTranslations()

  const getErrors = (): OrderErrorType[] => {
    if (!firm) {
      return []
    }

    const time: Date = checkoutForm.delivery_method.time ? new Date(checkoutForm.delivery_method.time) : new Date()
    const orderErrors: OrderErrorType[] = []

    cart.forEach((cartProduct: CartProduct) => {
      const product = products[cartProduct.id]

      if (!product) {
        return
      }

      const productCategoryAvailable = checkCategoryAvailability(product.categoryId, categories, time)
      const productAvailable = checkProductAvailability(product, time)

      if (!productCategoryAvailable || !productAvailable.inStock || productAvailable.snoozed) {
        orderErrors.push({
          variant: 'error',
          text: `${t('cart.errors.product_cannot_be_ordered')}: ${product.name[lang]}`,
          closable: false
        })
      }
    })

    if (
      loyaltyDiscountCanBeApplied
      && loyaltyTypeIsFreeProduct
      && !checkoutForm.loyaltyProductId
      && !!productsLoyaltyRewards.length
    ) {
      orderErrors.push({
        variant: 'warning',
        text: t('cart.errors.loyalty_product_missing'),
        closable: false
      })
    }

    if (!checkAll()) {
      orderErrors.push({
        variant: 'error',
        text: t('checkout.orders_only_during_opening_hours'),
        closable: false
      })
    }

    if (checkoutForm.delivery_method.method === null) {
      orderErrors.push({
        variant: 'error',
        text: t('checkout.errors.no_delivery_method_selected'),
        closable: false
      })
    }

    if (
      checkoutForm.delivery_method.method
      && !deliverMethodEnabledForFirm
    ) {
      orderErrors.push({
        variant: 'error',
        text: t('checkout.errors.delivery_method_not_valid'),
        closable: false
      })
    }

    if (
      checkoutForm.delivery_method.time === null
      && !checkoutForm.delivery_method.asap
      && checkoutForm.delivery_method.method !== DeliveryMethodNames.ON_THE_SPOT
    ) {
      orderErrors.push({
        variant: 'error',
        text: t('checkout.errors.no_time_selected'),
        closable: false
      })
    }

    if (checkoutForm.delivery_method.method === DeliveryMethodNames.DELIVERY) {
      if (!checkoutForm.delivery_method.address) {
        orderErrors.push({
          variant: 'warning',
          type: 'address',
          text: t('checkout.errors.select_an_address'),
          closable: false
        })
      }

      if (
        checkoutForm.delivery_method.minAmount
        && checkoutForm.delivery_method.minAmount > totalAmountOfCart + totalAmountOfCheckoutOptions
      ) {
        orderErrors.push({
          variant: 'error',
          text: t(
            'checkout.errors.min_amount_for_delivery_not_reached',
            {
              minimal_order_amount: `${firm.settings.currency.symbol} ${checkoutForm.delivery_method.minAmount.toFixed(2)}`
            }
          ),
          closable: false
        })
      } else if (
        checkoutForm.delivery_method.deliveryFreeFromPrice
        && checkoutForm.delivery_method.deliveryFreeFromPrice > totalAmountOfCart + totalAmountOfCheckoutOptions
      ) {
        orderErrors.push({
          variant: 'info',
          text: t(
            'checkout.info.free_delivery_from_price',
            {
              free_from_price: `${firm.settings.currency.symbol} ${checkoutForm.delivery_method.deliveryFreeFromPrice.toFixed(2)}`
            }
          ),
          closable: true
        })
      }

      if (firm.settings.max_cart_amount && firm.settings.max_cart_amount < cartTotalItems) {
        orderErrors.push({
          variant: 'error',
          text: t('checkout.errors.max_cart_amount', { max_cart_amount: firm.settings.max_cart_amount }),
          closable: false
        })
      }
    }

    return orderErrors
  }

  useEffect(() => {
    setErrors(getErrors())
  }, [
    cartTotalItems,
    checkoutForm,
    categories,
    loyaltyDiscountCanBeApplied,
    loyaltyTypeIsFreeProduct,
    deliverMethodEnabledForFirm,
    firm,
    totalAmountOfCart,
    totalAmountOfCheckoutOptions
  ])

  return {
    errors,
    getErrors
  }
}

export default useValidateOrder
